import React, { useState, useEffect } from "react";
import { X, HelpCircle } from "lucide-react";
import { DEFAULT_DESIGN } from "./surveyDefaults";

const SurveyPreview = ({
    surveyName,
    questions,
    afterSurveyAction = "reopen",
    customStyles,
    customText,
    presentationStyle = {
        style: "center-modal",
        position: "center",
        slideDirection: "right",
        cornerPosition: "bottom-right",
    },
}) => {
    // Use the first question for the preview
    const question = questions[0] || {
        text: "What would you like to know before making a purchase?",
        options: [],
        includeOther: false,
        type: "open-ended",
    };

    const isOpenEnded = question.type === "open-ended";
    const [selectedOption, setSelectedOption] = useState(undefined);
    const [textResponse, setTextResponse] = useState("");
    const [submitted, setSubmitted] = useState(false);
    const [closed, setClosed] = useState(false);
    const [firstTime, setFirstTime] = useState(true);
    const [visitorMessage, setVisitorMessage] = useState("");
    const [showFullSurvey, setShowFullSurvey] = useState(false);

    // state for dynamic height
    const [previewHeight, setPreviewHeight] = useState(window.innerHeight - 320);

    // update on window resize
    useEffect(() => {
        const handleResize = () => {
            setPreviewHeight(window.innerHeight - 320);
        };
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    useEffect(() => {
        if (presentationStyle.style === "corner-float") {
            setClosed(true);
        } else {
            setClosed(false);
        }
    }, [presentationStyle.style]);

    // ─── Default texts come from DEFAULT_DESIGN.text ────────────────────────
    const defaultTexts = DEFAULT_DESIGN.text;

    // Merge user-provided customText over defaultTexts
    const texts = {
        ...defaultTexts,
        ...customText,
        // If open-ended, we still want the placeholder to adapt ("Type your answer..." vs "Type your question...")
        inputPlaceholder: isOpenEnded
            ? customText?.inputPlaceholder ?? defaultTexts.inputPlaceholder
            : customText?.inputPlaceholder ?? "Type your question...",
    };

    // ─── Default styles come from DEFAULT_DESIGN (colors, typography, spacing, size) ─────────────────
    // deep-merge with any customStyles
    const mergedColors = {
        ...DEFAULT_DESIGN.colors,
        ...customStyles?.colors,
    };
    const mergedTypography = {
        ...DEFAULT_DESIGN.typography,
        ...customStyles?.typography,
    };
    const mergedSpacing = {
        ...DEFAULT_DESIGN.spacing,
        ...customStyles?.spacing,
    };
    const mergedSize = {
        ...DEFAULT_DESIGN.size,
        ...customStyles?.size,
    };

    const styles = {
        colors: mergedColors,
        typography: mergedTypography,
        spacing: mergedSpacing,
        size: mergedSize,
    };

    // Get dynamic positioning classes for corner float (only bottom positions)
    const getCornerFloatPositionClasses = () => {
        switch (presentationStyle.cornerPosition) {
            case "bottom-left":
                return "bottom-5 left-5";
            case "bottom-right":
            default:
                return "bottom-5 right-5";
        }
    };

    // Get dynamic positioning classes for side panel - now positioned at bottom
    const getSidePanelPositionClasses = () => {
        switch (presentationStyle.slideDirection) {
            case "left":
                return "bottom-5 left-5";
            case "right":
            default:
                return "bottom-5 right-5";
        }
    };

    // Get flex direction and alignment for corner float based on corner position
    const getCornerLayoutClasses = () => {
        const isLeft = presentationStyle.cornerPosition?.includes("left");

        return {
            flexDirection: "flex-col", // Always bottom for corner float
            alignItems: isLeft ? "items-start" : "items-end",
        };
    };

    // Get question bubble classes for corner float
    const getQuestionBubbleClasses = () => {
        const isLeft = presentationStyle.cornerPosition?.includes("left");
        return isLeft ? "rounded-l-lg rounded-tr-lg" : "rounded-r-lg rounded-tl-lg";
    };

    // Get question bubble tail for corner float
    const getQuestionBubbleTail = () => {
        const isLeft = presentationStyle.cornerPosition?.includes("left");
        return {
            position: isLeft ? "rounded-bl-lg" : "rounded-br-lg",
            transform: isLeft ? "rotate(45deg)" : "rotate(45deg)",
        };
    };

    // Handle submit
    const handleSubmit = () => {
        if (isOpenEnded ? textResponse.trim() : selectedOption) {
            setSubmitted(true);
        }
    };

    // Handle close
    const handleClose = () => {
        setClosed(true);
        setSubmitted(false);
        setSelectedOption(undefined);
        setTextResponse("");
        setVisitorMessage("");
    };

    // Reset preview
    const resetPreview = () => {
        setSubmitted(false);
        setClosed(false);
        setSelectedOption(undefined);
        setTextResponse("");
        setShowFullSurvey(false);
        setVisitorMessage("");
    };

    const BOX_SHADOWS = {
        none: "none",
        soft: "rgba(0, 0, 0, 0.05) 0px 4px 20px",
        default: "rgba(0, 0, 0, 0.1) 0px 4px 20px",
        medium: "rgba(0, 0, 0, 0.2) 0px 4px 20px",
    };

    // Render the main survey content
    const renderSurveyContent = (isCompact = false) => (
        <div
            className="bg-white border rounded-lg"
            style={{
                borderRadius: `${styles.size.borderRadius}px`,
                backgroundColor: styles.colors.popupBackground,
                borderColor: styles.colors.borderColor,
                borderWidth: `${styles.size.borderWidth}px`,
                width: `${presentationStyle.style === "center-modal" ? "92%" : "315px"}`,
                boxShadow: BOX_SHADOWS[styles.size.shadowIntensity] || BOX_SHADOWS.none,
            }}
        >
            <div
                className="relative"
                style={{
                    padding: `${styles.spacing.popupPaddingY}px ${styles.spacing.popupPaddingX}px`,
                }}
            >
                {!submitted ? (
                    <>
                        {/* Close button */}
                        <button
                            className="absolute right-4 top-4 flex items-center justify-center"
                            style={{ color: styles.colors.titleColor }}
                            onClick={handleClose}
                        >
                            <X size={16} />
                        </button>

                        {/* Header text */}
                        <h2
                            className="w-[96%] leading-tight pr-6"
                            style={{
                                color: styles.colors.titleColor,
                                fontSize: isCompact
                                    ? `${Math.max(14, styles.typography.titleSize - 2)}px`
                                    : `${styles.typography.titleSize}px`,
                                fontWeight:
                                    styles.typography.titleWeight === "normal"
                                        ? 400
                                        : styles.typography.titleWeight === "medium"
                                            ? 600
                                            : styles.typography.titleWeight === "semibold"
                                                ? 600
                                                : 700,
                                marginBottom: `${styles.spacing.titleBottomMargin}px`,
                                padding: "10px 25px",
                            }}
                        >
                            {question.text}
                        </h2>

                        {/* Survey content */}
                        <div
                            style={{
                                marginBottom: `${styles.spacing.buttonTopMargin}px`,
                                paddingInline: "25px",
                            }}
                        >
                            {isOpenEnded ? (
                                <textarea
                                    placeholder="Type your answer here..."
                                    className="w-full min-h-[80px] border p-2 resize-none rounded"
                                    style={{
                                        backgroundColor: styles.colors.otherInputBackground,
                                        borderColor: styles.colors.borderColor,
                                        fontSize: `${styles.typography.otherInputSize}px`,
                                    }}
                                    value={textResponse}
                                    onChange={(e) => setTextResponse(e.target.value)}
                                />
                            ) : (
                                <div className="space-y-2">
                                    {question.options.map((option, idx) => (
                                        <div key={idx} className="flex items-center space-x-2">
                                            <input
                                                type="radio"
                                                id={`option-${idx}`}
                                                name="survey-option"
                                                value={`option-${idx}`}
                                                className="h-4 w-4"
                                                style={{ accentColor: styles.colors.selectedColor }}
                                                checked={selectedOption === `option-${idx}`}
                                                onChange={() => setSelectedOption(`option-${idx}`)}
                                            />
                                            <label
                                                htmlFor={`option-${idx}`}
                                                className="cursor-pointer"
                                                style={{
                                                    color: styles.colors.optionTextColor,
                                                    fontSize: isCompact
                                                        ? `${Math.max(12, styles.typography.optionSize - 2)}px`
                                                        : `${styles.typography.optionSize}px`,
                                                    fontWeight:
                                                        styles.typography.optionWeight === "normal"
                                                            ? 400
                                                            : styles.typography.optionWeight === "medium"
                                                                ? 600
                                                                : styles.typography.optionWeight === "semibold"
                                                                    ? 600
                                                                    : 700,
                                                }}
                                            >
                                                {option}
                                            </label>
                                        </div>
                                    ))}

                                    {question.includeOther && (
                                        <div className="flex flex-col space-y-2">
                                            <div className="flex items-center space-x-2">
                                                <input
                                                    type="radio"
                                                    id="other"
                                                    name="survey-option"
                                                    value="other"
                                                    className="h-4 w-4"
                                                    style={{ accentColor: styles.colors.selectedColor }}
                                                    checked={selectedOption === "other"}
                                                    onChange={() => setSelectedOption("other")}
                                                />
                                                <label
                                                    htmlFor="other"
                                                    className="cursor-pointer"
                                                    style={{
                                                        color: styles.colors.optionTextColor,
                                                        fontSize: isCompact
                                                            ? `${Math.max(12, styles.typography.optionSize - 2)}px`
                                                            : `${styles.typography.optionSize}px`,
                                                        fontWeight:
                                                            styles.typography.optionWeight === "normal"
                                                                ? 400
                                                                : styles.typography.optionWeight === "medium"
                                                                    ? 600
                                                                    : styles.typography.optionWeight === "semibold"
                                                                        ? 600
                                                                        : 700,
                                                    }}
                                                >
                                                    Other
                                                </label>
                                            </div>

                                            {selectedOption === "other" && (
                                                <div className="pl-6 pt-1">
                                                    <textarea
                                                        placeholder="Type your answer..."
                                                        className="w-full min-h-[60px] border p-2 resize-none rounded"
                                                        style={{
                                                            backgroundColor: styles.colors.otherInputBackground,
                                                            borderColor: styles.colors.borderColor,
                                                            fontSize: `${styles.typography.otherInputSize}px`,
                                                        }}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Submit button */}
                        <div style={{ paddingInline: "25px" }}>
                            <button
                                className="w-full py-2 px-4 rounded"
                                onClick={handleSubmit}
                                style={{
                                    marginTop: styles.spacing.buttonTopMargin,
                                    marginBottom: "12px",
                                    backgroundColor: styles.colors.buttonBackground,
                                    color: styles.colors.buttonTextColor,
                                    fontSize: isCompact
                                        ? `${Math.max(12, styles.typography.buttonTextSize - 2)}px`
                                        : `${styles.typography.buttonTextSize}px`,
                                    fontWeight:
                                        styles.typography.buttonTextWeight === "normal"
                                            ? 400
                                            : styles.typography.buttonTextWeight === "medium"
                                                ? 600
                                                : styles.typography.buttonTextWeight === "semibold"
                                                    ? 600
                                                    : 700,
                                }}
                            >
                                {texts.submitButtonText || "Share Response"}
                            </button>
                        </div>
                    </>
                ) : (
                    <>
                        {/* Chat response state */}
                        <div
                            className="flex items-center justify-between mb-4"
                            style={{ padding: "10px 25px" }}
                        >
                            <h3
                                className="font-medium"
                                style={{
                                    color: styles.colors.titleColor,
                                    fontSize: isCompact
                                        ? `${Math.max(14, styles.typography.titleSize - 2)}px`
                                        : `${styles.typography.titleSize}px`,
                                    fontWeight:
                                        styles.typography.titleWeight === "normal"
                                            ? 400
                                            : styles.typography.titleWeight === "medium"
                                                ? 600
                                                : styles.typography.titleWeight === "semibold"
                                                    ? 600
                                                    : 700,
                                }}
                            >
                                {texts.assistantTitle || "Ask us anything"}
                            </h3>
                            <button
                                className="flex items-center justify-center"
                                style={{ color: styles.colors.titleColor }}
                                onClick={handleClose}
                            >
                                <X size={16} />
                            </button>
                        </div>

                        <div style={{ paddingInline: "25px" }}>
                            <div
                                className="p-3 rounded-lg mb-4"
                                style={{
                                    backgroundColor: styles.colors.aiMessageBackground,
                                    color: styles.colors.aiMessageText,
                                }}
                            >
                                <p
                                    style={{
                                        fontSize: isCompact
                                            ? `${Math.max(12, styles.typography.optionSize - 2)}px`
                                            : `${styles.typography.optionSize}px`,
                                    }}
                                >
                                    I understand you're interested in customer reviews and
                                    ratings. Unfortunately, we don't have specific reviews listed
                                    for this product at the moment.
                                </p>
                            </div>
                        </div>

                        <div className="relative" style={{ paddingInline: "25px" }}>
                            <textarea
                                placeholder={texts.inputPlaceholder || "Type your answer..."}
                                className="w-full min-h-[40px] pr-10 resize-none border p-2 rounded"
                                style={{
                                    backgroundColor: "#f9fafb",
                                    borderColor: styles.colors.borderColor,
                                    fontSize: isCompact
                                        ? `${Math.max(12, styles.typography.otherInputSize - 2)}px`
                                        : `${styles.typography.otherInputSize}px`,
                                }}
                                value={visitorMessage}
                                onChange={(e) => setVisitorMessage(e.target.value)}
                            />
                            <button
                                className="absolute right-8 bottom-3 rounded-full p-1"
                                style={{ backgroundColor: styles.colors.buttonBackground }}
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="14"
                                    height="14"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    style={{ color: styles.colors.buttonTextColor }}
                                >
                                    <path d="M22 2 11 13" />
                                    <polygon points="22 2 15 22 11 13 2 9 22 2" />
                                </svg>
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );

    // Render floating circle design with side panel
    const renderFloatingCircle = () => {
        const layoutClasses = getCornerLayoutClasses();
        const bubbleClasses = getQuestionBubbleClasses();
        const bubbleTail = getQuestionBubbleTail();

        return (
            <div>
                {closed ? (
                    <div className={`absolute ${getCornerFloatPositionClasses()}`}>
                        <div
                            className={`flex gap-3 ${layoutClasses.flexDirection} ${layoutClasses.alignItems}`}
                        >
                            {/* Question bubble - positioned relative to circle based on corner */}
                            {firstTime && (
                                <div
                                    className={`relative p-3 shadow-lg border max-w-xs cursor-pointer ${bubbleClasses}`}
                                    style={{
                                        backgroundColor: styles.colors.popupBackground,
                                        borderColor: styles.colors.borderColor,
                                    }}
                                    onClick={() => {
                                        setClosed(false);
                                        setFirstTime(false);
                                    }}
                                >
                                    <p
                                        className="text-sm"
                                        style={{
                                            color: styles.colors.titleColor,
                                            fontSize: "13px",
                                        }}
                                    >
                                        {question.text}
                                    </p>
                                    {/* Chat bubble tail pointing to circle */}
                                    <div
                                        className={`w-3 h-3 ${bubbleTail.position}`}
                                        style={{
                                            backgroundColor: styles.colors.popupBackground,
                                            borderBottom: `1px solid ${styles.colors.borderColor}`,
                                            borderRight: `1px solid ${styles.colors.borderColor}`,
                                            transform: bubbleTail.transform,
                                        }}
                                    />
                                </div>
                            )}

                            {/* Circle button with question mark */}
                            <button
                                onClick={() => {
                                    setClosed(false);
                                    setFirstTime(false);
                                }}
                                className="w-14 h-14 rounded-full shadow-lg flex items-center justify-center hover:scale-105 transition-transform flex-shrink-0"
                                style={{
                                    backgroundColor: styles.colors.buttonBackground,
                                    color: styles.colors.buttonTextColor,
                                }}
                            >
                                <HelpCircle size={24} />
                            </button>
                        </div>
                    </div>
                ) : null}

                {/* Side panel when survey is open - positioned at the selected corner */}
                {!closed && (
                    <div className={`absolute w-max ${getCornerFloatPositionClasses()}`}>
                        {renderSurveyContent(true)}
                    </div>
                )}
            </div>
        );
    };

    // Render side panel design - uses slideDirection for positioning at bottom
    const renderSidePanel = () =>
        !closed && (
            <div className={`absolute ${getSidePanelPositionClasses()}`}>
                {renderSurveyContent(true)}
            </div>
        );

    // Render center modal as bottom banner with visible sides
    const renderCenterModal = () =>
        !closed && (
            <div className="absolute bottom-0 left-0 w-full flex justify-center">
                {renderSurveyContent(false)}
            </div>
        );

    // Simulate a website with content
    const renderWebsiteContent = () => (
        <div className="bg-white min-h-[700px]">
            {/* Header */}
            <div className="bg-gray-100 p-4 border-b">
                <div className="flex items-center justify-between max-w-6xl mx-auto">
                    <div className="w-32 h-8 bg-gray-300 rounded"></div>
                    <div className="flex gap-4">
                        <div className="w-20 h-8 bg-gray-200 rounded"></div>
                        <div className="w-20 h-8 bg-gray-200 rounded"></div>
                        <div className="w-20 h-8 bg-gray-200 rounded"></div>
                    </div>
                </div>
            </div>

            {/* Main content */}
            <div className="max-w-6xl mx-auto p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                    <div className="h-48 bg-gray-200 rounded-lg"></div>
                    <div className="h-48 bg-gray-200 rounded-lg"></div>
                    <div className="h-48 bg-gray-200 rounded-lg"></div>
                </div>

                <div className="space-y-4 mb-8">
                    <div className="h-6 bg-gray-200 rounded w-3/4"></div>
                    <div className="h-6 bg-gray-200 rounded w-1/2"></div>
                    <div className="h-6 bg-gray-200 rounded w-5/6"></div>
                    <div className="h-6 bg-gray-200 rounded w-2/3"></div>
                </div>

                <div className="grid grid-cols-2 gap-6 mb-8">
                    <div className="h-32 bg-gray-200 rounded"></div>
                    <div className="h-32 bg-gray-200 rounded"></div>
                </div>

                <div className="space-y-3">
                    <div className="h-4 bg-gray-200 rounded w-full"></div>
                    <div className="h-4 bg-gray-200 rounded w-4/5"></div>
                    <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                    <div className="h-4 bg-gray-200 rounded w-5/6"></div>
                    <div className="h-4 bg-gray-200 rounded w-2/3"></div>
                </div>
            </div>

            {/* Footer space */}
            <div className="h-32"></div>
        </div>
    );

    return (
        <div className="w-full flex flex-col items-center justify-center sticky top-24 space-y-4">
            <div className="w-full overflow-hidden border-none shadow-lg rounded-lg border bg-card text-card-foreground">
                <div className="pb-0 bg-gradient-to-r from-[#f3f4f6] to-white relative border-b flex flex-col space-y-1.5 p-6">
                    <div className="pl-2">
                        <h3 className="font-semibold bg-clip-text text-transparent bg-gradient-to-r from-[#1A1F2C] to-[#6E59A5] text-2xl leading-none tracking-tight">
                            Live Preview
                        </h3>
                        <p className="text-sm mt-1 pb-3 leading-relaxed text-[#8E9196]">
                            This is how your survey will appear to visitors on your website.
                            Use the controls below to test different states.
                        </p>
                    </div>
                </div>

                <div className="p-0 pt-0">
                    <div
                        className="relative overflow-hidden"
                        style={{ height: `${previewHeight}px`, width: "100%" }}
                    >
                        {/* Simulated website */}
                        <div className="absolute inset-0 overflow-y-auto">
                            {renderWebsiteContent()}
                        </div>

                        {/* Survey overlays based on presentation style - positioned within the preview container */}
                        <>
                            {/* Center Modal - bottom center with visible sides */}
                            {presentationStyle.style === "center-modal" && renderCenterModal()}

                            {/* Corner Float - floating circle with chat bubble and side panel */}
                            {presentationStyle.style === "corner-float" && renderFloatingCircle()}

                            {/* Side Panel - compact version positioned at bottom based on slideDirection */}
                            {presentationStyle.style === "side-panel" && renderSidePanel()}
                        </>

                        {/* Reopening button when closed - positioned at bottom center */}
                        {closed && afterSurveyAction !== "nothing" && presentationStyle.style != "corner-float" && (
                            <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2">
                                <button
                                    onClick={resetPreview}
                                    className="py-2 px-4 rounded shadow-lg"
                                    style={{
                                        backgroundColor: styles.colors.buttonBackground,
                                        color: styles.colors.buttonTextColor,
                                        fontSize: `${styles.typography.buttonTextSize}px`,
                                        fontWeight:
                                            styles.typography.buttonTextWeight === "normal"
                                                ? 400
                                                : styles.typography.buttonTextWeight === "medium"
                                                    ? 600
                                                    : styles.typography.buttonTextWeight === "semibold"
                                                        ? 600
                                                        : 700,
                                    }}
                                >
                                    {texts.chatButtonText || "Tell us what's missing"}
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Preview controls */}
                    <div className="flex gap-2 justify-center py-4 border-t bg-gray-50">
                        <button
                            onClick={() => resetPreview()}
                            className="text-xs bg-gradient-to-r from-gray-100 to-gray-200 hover:from-gray-200 hover:to-gray-300 text-gray-700 px-4 py-1.5 rounded-md font-medium shadow-sm transition-all duration-200"
                        >
                            Show Survey
                        </button>
                        <button
                            onClick={() => {
                                setSubmitted(true);
                                setClosed(false);
                            }}
                            className="text-xs bg-gradient-to-r from-gray-100 to-gray-200 hover:from-gray-200 hover:to-gray-300 text-gray-700 px-4 py-1.5 rounded-md font-medium shadow-sm transition-all duration-200"
                        >
                            Show Chat
                        </button>
                        {afterSurveyAction !== "nothing" && (
                            <button
                                onClick={() => setClosed(true)}
                                className="text-xs bg-gradient-to-r from-gray-100 to-gray-200 hover:from-gray-200 hover:to-gray-300 text-gray-700 px-4 py-1.5 rounded-md font-medium shadow-sm transition-all duration-200"
                            >
                                Show Closed
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SurveyPreview;
